/**
 * InlineDetailPanel — Renders as <tr> rows in the parent table.
 * Each child row has the SAME number of cells as the parent,
 * with data placed in specific column positions so vertical
 * borders align perfectly.
 */

import { useState, useEffect, useRef, useId } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/features/pos/hooks/useReducedMotion";
import { useItemWarehouseStock } from "../../api/useItemStock";
import { useAllItemPrices, useItemPricingRules } from "../../api/pricingSummaryHooks";
import { StockTab } from "./StockTab";
import { PriceListTab } from "./PriceListTab";
import { RulesTab } from "./RulesTab";
import { useTableStyles } from "./useTableStyles";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

type TabId = "stock" | "pricelist" | "rules";

export interface InlineDetailPanelProps {
  itemCode: string;
  itemName?: string;
  detailPath: string;
  onClose?: () => void;
  columns: SubRowColumnInfo;
}

const TABS: { id: TabId; label: string }[] = [
  { id: "stock", label: "Stock" },
  { id: "pricelist", label: "Price List" },
  { id: "rules", label: "Rules" },
];

const PANEL_KEYFRAMES = `
@keyframes idp-slide-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}`;

export function InlineDetailPanel({
  itemCode,
  itemName,
  detailPath,
  onClose,
  columns,
}: InlineDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>("stock");
  const reducedMotion = useReducedMotion();
  const panelId = useId();
  const firstTabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const delay = reducedMotion ? 0 : 160;
    const timer = setTimeout(() => firstTabRef.current?.focus(), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector('[role="dialog"]') || document.querySelector('[data-inline-form-open]')) return;
      onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let nextIndex = -1;
    if (e.key === "ArrowRight") nextIndex = (index + 1) % TABS.length;
    if (e.key === "ArrowLeft") nextIndex = (index - 1 + TABS.length) % TABS.length;
    if (nextIndex >= 0) {
      e.preventDefault();
      setActiveTab(TABS[nextIndex].id);
      const tablist = e.currentTarget.closest('[role="tablist"]');
      const buttons = tablist?.querySelectorAll('[role="tab"]');
      (buttons?.[nextIndex] as HTMLElement)?.focus();
    }
  };

  const { textSize } = useTableStyles();
  const tabFontSize = textSize === "small" ? "10px" : textSize === "large" ? "13px" : "12px";

  const stockQuery = useItemWarehouseStock(itemCode, activeTab === "stock");
  const pricesQuery = useAllItemPrices(itemCode, activeTab === "pricelist");
  const rulesQuery = useItemPricingRules(itemCode, activeTab === "rules");

  const animClass = reducedMotion ? "" : "[animation:idp-slide-in_150ms_ease-out_both]";

  return (
    <>
      <style>{PANEL_KEYFRAMES}</style>
      {/* Tab bar row */}
      <tr
        className={cn(
          "border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-900/60",
          animClass,
        )}
        aria-label={`Details for ${itemName || itemCode}`}
      >
        <td colSpan={columns.columnCount} className="!p-0 !border-r-0">
          <div className="flex items-center justify-between px-3 py-1">
            <div role="tablist" className="flex items-center gap-0.5">
              {TABS.map((tab, i) => (
                <button
                  key={tab.id}
                  ref={i === 0 ? firstTabRef : undefined}
                  id={`${panelId}-tab-${tab.id}`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`${panelId}-tabpanel`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, i)}
                  style={{ fontSize: tabFontSize }}
                  className={cn(
                    "font-medium px-2.5 py-0.5 rounded-xs",
                    activeTab === tab.id
                      ? "text-foreground bg-white dark:bg-neutral-800 shadow-sm border border-neutral-200 dark:border-neutral-600"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <Link
              to={detailPath}
              style={{ fontSize: tabFontSize }}
              className="text-muted-foreground hover:text-foreground"
            >
              View detail &rarr;
            </Link>
          </div>
        </td>
      </tr>

      {/* Child data rows — each row has same cell count as parent */}
      <tr className={cn("border-b border-neutral-200 dark:border-neutral-700", animClass)}>
        <td
          colSpan={columns.columnCount}
          className="!p-0 !border-r-0"
          id={`${panelId}-tabpanel`}
          role="tabpanel"
          aria-labelledby={`${panelId}-tab-${activeTab}`}
        >
          {activeTab === "stock" && (
            <StockTab
              data={stockQuery.data}
              isLoading={stockQuery.isLoading}
              isError={stockQuery.isError}
              refetch={stockQuery.refetch}
              columnWidths={columns.columnWidths}
              itemCode={itemCode}
            />
          )}
          {activeTab === "pricelist" && (
            <PriceListTab
              data={pricesQuery.data}
              isLoading={pricesQuery.isLoading}
              isError={pricesQuery.isError}
              refetch={pricesQuery.refetch}
              itemCode={itemCode}
              columnWidths={columns.columnWidths}
            />
          )}
          {activeTab === "rules" && (
            <RulesTab
              data={rulesQuery.data}
              isLoading={rulesQuery.isLoading}
              isError={rulesQuery.isError}
              refetch={rulesQuery.refetch}
              columnWidths={columns.columnWidths}
            />
          )}
        </td>
      </tr>
    </>
  );
}
